import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { FaDollarSign, FaClock, FaChartLine } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';

const ROICalculator: React.FC = () => {
  const { t } = useLanguage();
  const [hoursPerWeek, setHoursPerWeek] = useState(10);
  const [hourlyCost, setHourlyCost] = useState(25);
  const [automationPercent, setAutomationPercent] = useState(80);

  // Admin mode state
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [pricingPercent, setPricingPercent] = useState(35);
  const [fixedPrice, setFixedPrice] = useState<number | null>(null);
  const [useFixedPrice, setUseFixedPrice] = useState(false);

  // Keyboard shortcut for admin mode: Ctrl+Shift+A or Cmd+Shift+A
  React.useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        setIsAdminMode(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  const calculations = useMemo(() => {
    const exchangeRate = 18; // USD to MXN
    const annualCost = hoursPerWeek * hourlyCost * 50; // 50 weeks per year
    const annualSavings = annualCost * (automationPercent / 100);

    // Calculate project cost based on admin mode
    let projectCost = 5000; // Default example cost
    if (isAdminMode) {
      if (useFixedPrice && fixedPrice) {
        projectCost = fixedPrice;
      } else {
        projectCost = annualSavings * (pricingPercent / 100);
      }
    }

    const netClientBenefit = annualSavings - projectCost;
    const roi = ((annualSavings - projectCost) / projectCost) * 100;

    return {
      annualCost,
      annualSavings,
      projectCost,
      netClientBenefit,
      roi,
      paybackMonths: (projectCost / (annualSavings / 12)).toFixed(1),
      // MXN conversions
      annualSavingsMXN: annualSavings * exchangeRate,
      projectCostMXN: projectCost * exchangeRate,
      netClientBenefitMXN: netClientBenefit * exchangeRate
    };
  }, [hoursPerWeek, hourlyCost, automationPercent, isAdminMode, pricingPercent, fixedPrice, useFixedPrice]);

  return (
    <section className="py-20 px-6 relative overflow-hidden" style={{ backgroundColor: 'var(--color-background)' }}>
      {/* Background blur elements */}
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-block mb-6 relative"
          >
            <span
              onClick={() => {
                // Triple tap to toggle admin mode on mobile
                const now = Date.now();
                const tapTimes = (window as any).__adminTapTimes || [];
                tapTimes.push(now);
                (window as any).__adminTapTimes = tapTimes.filter((time: number) => now - time < 1000);

                if ((window as any).__adminTapTimes.length >= 3) {
                  setIsAdminMode(prev => !prev);
                  (window as any).__adminTapTimes = [];
                }
              }}
              className="px-6 py-2 rounded-full text-sm font-semibold glass cursor-pointer select-none"
              style={{ color: 'var(--color-success)' }}
            >
              {t.calculator.roiCalculator}
            </span>

            {/* Admin Mode Indicator */}
            {isAdminMode && (
              <motion.span
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                onClick={() => setIsAdminMode(false)}
                className="absolute -top-2 -right-2 px-2 py-0.5 rounded-full glass text-xs font-semibold cursor-pointer"
                style={{
                  border: '1px solid #f59e0b',
                  color: '#f59e0b',
                  animation: 'adminPulse 2s ease-in-out infinite'
                }}
                title="Click to exit admin mode"
              >
                🔒
              </motion.span>
            )}
          </motion.div>

          <h2
            className="text-4xl md:text-5xl font-bold mb-4"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {t.calculator.title}
          </h2>
          <p
            className="text-xl max-w-3xl mx-auto"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            {t.calculator.subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Controls - Left 2 columns */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-2 glass p-8"
          >
            <h3 className="text-2xl font-bold mb-8" style={{ color: 'var(--color-text-primary)' }}>
              {t.calculator.yourCurrentSituation}
            </h3>

            <div className="space-y-8">
              {/* Hours per week */}
              <div>
                <label
                  className="block text-lg font-semibold mb-4"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  {React.createElement(FaClock, { className: "inline mr-2", style: { color: 'var(--color-primary)' } })}
                  {t.calculator.hoursPerWeek} <span style={{ color: 'var(--color-primary)' }}>{hoursPerWeek}h</span>
                </label>
                <input
                  type="range"
                  min="1"
                  max="40"
                  value={hoursPerWeek}
                  onChange={(e) => setHoursPerWeek(Number(e.target.value))}
                  className="w-full h-3 rounded-full appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, var(--color-primary) 0%, var(--color-primary) ${(hoursPerWeek / 40) * 100}%, rgba(255,255,255,0.2) ${(hoursPerWeek / 40) * 100}%, rgba(255,255,255,0.2) 100%)`
                  }}
                />
                <div className="flex justify-between text-sm mt-2" style={{ color: 'var(--color-text-light)' }}>
                  <span>1h</span>
                  <span>40h</span>
                </div>
              </div>

              {/* Hourly cost */}
              <div>
                <label
                  className="block text-lg font-semibold mb-4"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  {React.createElement(FaDollarSign, { className: "inline mr-2", style: { color: 'var(--color-primary)' } })}
                  {t.calculator.hourlyCost}
                </label>
                <div className="relative glass-medium p-4">
                  <span
                    className="absolute left-6 top-1/2 transform -translate-y-1/2 text-3xl font-bold"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    $
                  </span>
                  <input
                    type="number"
                    min="10"
                    max="500"
                    value={hourlyCost}
                    onChange={(e) => setHourlyCost(Number(e.target.value))}
                    className="w-full pl-12 pr-4 text-3xl font-bold bg-transparent border-0 focus:outline-none"
                    style={{
                      color: 'var(--color-text-primary)'
                    }}
                  />
                </div>
              </div>

              {/* Automation percentage */}
              <div>
                <label
                  className="block text-lg font-semibold mb-4"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  {React.createElement(FaChartLine, { className: "inline mr-2", style: { color: 'var(--color-success)' } })}
                  {t.calculator.automationPotential} <span style={{ color: 'var(--color-success)' }}>{automationPercent}%</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={automationPercent}
                  onChange={(e) => setAutomationPercent(Number(e.target.value))}
                  className="w-full h-3 rounded-full appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, var(--color-success) 0%, var(--color-success) ${automationPercent}%, rgba(255,255,255,0.2) ${automationPercent}%, rgba(255,255,255,0.2) 100%)`
                  }}
                />
                <div className="flex justify-between text-sm mt-2" style={{ color: 'var(--color-text-light)' }}>
                  <span>0%</span>
                  <span>100%</span>
                </div>
              </div>

              {/* Admin Pricing Controls */}
              {isAdminMode && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="pt-6 border-t-2"
                  style={{ borderColor: '#f59e0b' }}
                >
                  <h4 className="text-lg font-bold mb-4" style={{ color: '#f59e0b' }}>
                    🔒 {t.calculator.pricingControls}
                  </h4>

                  {/* Pricing Mode Toggle */}
                  <div className="flex gap-2 mb-6">
                    <button
                      onClick={() => setUseFixedPrice(false)}
                      className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-all ${
                        !useFixedPrice ? 'glass-strong' : 'glass'
                      }`}
                      style={{
                        border: !useFixedPrice ? '2px solid #f59e0b' : '1px solid rgba(255,255,255,0.2)',
                        color: !useFixedPrice ? '#f59e0b' : 'var(--color-text-secondary)'
                      }}
                    >
                      {t.calculator.percentOfSavings}
                    </button>
                    <button
                      onClick={() => setUseFixedPrice(true)}
                      className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-all ${
                        useFixedPrice ? 'glass-strong' : 'glass'
                      }`}
                      style={{
                        border: useFixedPrice ? '2px solid #f59e0b' : '1px solid rgba(255,255,255,0.2)',
                        color: useFixedPrice ? '#f59e0b' : 'var(--color-text-secondary)'
                      }}
                    >
                      {t.calculator.fixedPrice}
                    </button>
                  </div>

                  {!useFixedPrice ? (
                    <div>
                      <label
                        className="block text-sm font-semibold mb-3"
                        style={{ color: '#f59e0b' }}
                      >
                        {t.calculator.pricing} <span className="text-xl">{pricingPercent}%</span> {t.calculator.ofAnnualSavings}
                      </label>
                      <input
                        type="range"
                        min="20"
                        max="50"
                        value={pricingPercent}
                        onChange={(e) => setPricingPercent(Number(e.target.value))}
                        className="w-full h-3 rounded-full appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, #f59e0b 0%, #f59e0b ${((pricingPercent - 20) / 30) * 100}%, rgba(255,255,255,0.2) ${((pricingPercent - 20) / 30) * 100}%, rgba(255,255,255,0.2) 100%)`
                        }}
                      />
                      <div className="flex justify-between text-xs mt-2" style={{ color: '#f59e0b' }}>
                        <span>20%</span>
                        <span>50%</span>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label
                        className="block text-sm font-semibold mb-3"
                        style={{ color: '#f59e0b' }}
                      >
                        {t.calculator.fixedProjectPrice}
                      </label>
                      <div className="relative glass-medium p-3" style={{ border: '2px solid #f59e0b' }}>
                        <span
                          className="absolute left-4 top-1/2 transform -translate-y-1/2 text-2xl font-bold"
                          style={{ color: '#f59e0b' }}
                        >
                          $
                        </span>
                        <input
                          type="number"
                          min="1000"
                          max="100000"
                          step="500"
                          value={fixedPrice || ''}
                          onChange={(e) => setFixedPrice(Number(e.target.value) || null)}
                          placeholder={t.calculator.enterAmount}
                          className="w-full pl-10 pr-4 text-2xl font-bold bg-transparent border-0 focus:outline-none"
                          style={{
                            color: '#f59e0b'
                          }}
                        />
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          </motion.div>

          {/* Results - Right 3 columns */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-3 space-y-6"
          >
            <h3 className="text-2xl font-bold mb-6" style={{ color: 'var(--color-text-primary)' }}>
              {t.calculator.yourPotentialImpact}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Annual Cost */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="glass p-6"
              >
                <h4 className="text-sm font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                  {t.calculator.currentAnnualCost}
                </h4>
                <p className="text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                  ${calculations.annualCost.toLocaleString()}
                </p>
              </motion.div>

              {/* ROI */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                className={`glass p-6 relative overflow-hidden transition-all duration-500 ${
                  isAdminMode ? 'ring-2 ring-primary' : ''
                }`}
                style={{
                  opacity: isAdminMode ? 1 : 0.9
                }}
              >
                <motion.div
                  className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(135deg, var(--color-primary), var(--color-accent))',
                    opacity: isAdminMode ? 0.2 : 0.1
                  }}
                />
                <div className="relative z-10">
                  <h4 className="text-sm font-semibold mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                    {t.calculator.returnOnInvestment} {isAdminMode && t.calculator.withYourPricing}
                  </h4>
                  <p className="text-3xl font-bold" style={{ color: 'var(--color-primary)' }}>
                    {calculations.roi > 0 ? '+' : ''}{calculations.roi.toFixed(0)}%
                  </p>
                  <p className="text-sm mt-1" style={{ color: 'var(--color-text-light)' }}>
                    ~{calculations.paybackMonths} {t.calculator.monthPayback}
                  </p>

                  {/* Admin-only: Show old ROI without savings deduction */}
                  {isAdminMode && (() => {
                    const oldRoi = ((calculations.annualSavings - 5000) / 5000) * 100;
                    return (
                      <div className="mt-3 pt-3 border-t border-white/20">
                        <p className="text-xs opacity-60" style={{ color: 'var(--color-text-light)' }}>
                          {t.calculator.oldRoiGeneric} +{oldRoi.toFixed(0)}%
                        </p>
                      </div>
                    );
                  })()}
                </div>
              </motion.div>
            </div>

            {/* Annual Savings - Smaller and blurred in admin mode */}
            <motion.div
              whileHover={{ scale: isAdminMode ? 1 : 1.02, y: isAdminMode ? 0 : -5 }}
              className={`relative overflow-hidden glass-strong transition-all duration-500 ${
                isAdminMode ? 'p-4 opacity-40 blur-sm' : 'p-8'
              }`}
            >
              <motion.div
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(135deg, var(--color-success), var(--color-success-light))',
                  opacity: 0.15
                }}
              />
              <div className="relative z-10">
                <h3 className={`font-semibold mb-2 ${isAdminMode ? 'text-sm' : 'text-lg mb-3'}`} style={{ color: 'var(--color-text-secondary)' }}>
                  {t.calculator.annualSavingsPotential}
                </h3>
                <p className={`font-bold mb-2 ${isAdminMode ? 'text-2xl' : 'text-5xl'}`} style={{ color: 'var(--color-success)' }}>
                  ${calculations.annualSavings.toLocaleString()} USD
                </p>
                <p className={`${isAdminMode ? 'text-sm' : 'text-lg'} mb-2`} style={{ color: 'var(--color-text-secondary)' }}>
                  ${calculations.annualSavingsMXN.toLocaleString()} MXN
                </p>
                {!isAdminMode && (
                  <p className="text-lg" style={{ color: 'var(--color-text-secondary)' }}>
                    ${(calculations.annualSavings / 12).toLocaleString()} USD {t.calculator.perMonth}
                  </p>
                )}
              </div>
            </motion.div>

            {/* Admin-only: Your Investment & Client Net Benefit - Side by side */}
            {isAdminMode && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Your Investment */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.02, y: -5 }}
                  className="relative overflow-hidden p-6 glass-strong"
                  style={{
                    border: '2px solid #f59e0b',
                    boxShadow: '0 0 30px rgba(245, 158, 11, 0.2)'
                  }}
                >
                  <div className="relative z-10">
                    <h3 className="text-sm font-semibold mb-2 flex items-center gap-2" style={{ color: '#f59e0b' }}>
                      🔒 {t.calculator.yourInvestment}
                    </h3>
                    <p className="text-4xl font-bold mb-1" style={{ color: '#f59e0b' }}>
                      ${calculations.projectCost.toLocaleString()} USD
                    </p>
                    <p className="text-sm mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                      ${calculations.projectCostMXN.toLocaleString()} MXN
                    </p>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      {useFixedPrice ? t.calculator.fixedPricing : `${pricingPercent}% ${t.calculator.ofAnnualSavings}`}
                    </p>
                  </div>
                </motion.div>

                {/* Client Net Benefit */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.02, y: -5 }}
                  className="relative overflow-hidden p-6 glass-strong"
                  style={{
                    border: '2px solid #10b981',
                    boxShadow: '0 0 30px rgba(16, 185, 129, 0.2)'
                  }}
                >
                  <div className="relative z-10">
                    <h3 className="text-sm font-semibold mb-2 flex items-center gap-2" style={{ color: 'var(--color-success)' }}>
                      🔒 {t.calculator.clientNetBenefit}
                    </h3>
                    <p className="text-4xl font-bold mb-1" style={{ color: 'var(--color-success)' }}>
                      ${calculations.netClientBenefit.toLocaleString()} USD
                    </p>
                    <p className="text-sm mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                      ${calculations.netClientBenefitMXN.toLocaleString()} MXN
                    </p>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      ${(calculations.netClientBenefit / 12).toLocaleString()} USD {t.calculator.perMonth}
                    </p>
                  </div>
                </motion.div>
              </div>
            )}

            {/* Visual comparison - Only in admin mode */}
            {isAdminMode && (
              <div className="glass p-6">
                <h4
                  className="text-sm font-semibold mb-4"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {t.calculator.investmentVsReturn}
                </h4>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span style={{ color: 'var(--color-text-secondary)' }}>
                        {t.calculator.projectInvestment}
                      </span>
                      <span style={{ color: '#f59e0b' }}>
                        ${calculations.projectCost.toLocaleString()}
                      </span>
                    </div>
                    <div className="h-10 rounded-full overflow-hidden glass-medium">
                      <motion.div
                        className="h-full rounded-full"
                        style={{
                          background: 'linear-gradient(90deg, #f59e0b, #f97316)'
                        }}
                        initial={{ width: 0 }}
                        whileInView={{
                          width: `${Math.min((calculations.projectCost / calculations.annualSavings) * 100, 100)}%`
                        }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.3 }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span style={{ color: 'var(--color-text-secondary)' }}>{t.calculator.clientNetBenefit}</span>
                      <span style={{ color: 'var(--color-success)' }}>
                        ${calculations.netClientBenefit.toLocaleString()}
                      </span>
                    </div>
                    <div className="h-10 rounded-full overflow-hidden glass-medium">
                      <motion.div
                        className="h-full rounded-full"
                        style={{
                          background: 'linear-gradient(90deg, var(--color-success), var(--color-success-light))'
                        }}
                        initial={{ width: 0 }}
                        whileInView={{ width: '100%' }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.5 }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;
